import { createNamespacedHelpers } from 'vuex';
import { mapEntity, prefixTypeFactory } from '../../utils/StoreHelper';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
  prefixType as prefixGlassType,
} from './configurator/glasses';
import { DEFAULT_SIZE_ID, SizeId } from '../../api/static/sizes';
import { DEFAULT_COLOR_ID } from '../../api/static/colors';
import { EXPRESS_DELIVERY_PERCENTAGE } from '../../api/static/delivery';
import { ZoneType } from '../../api/static/zones';
import {
  DEFAULT_PRINT_TYPE_ID,
  PrintTypeId,
} from '../../api/static/printTypes';
import {
  getColorId,
  getCustomizationGroupId,
  getProductId,
  getQuantityId,
  getScreenPrintingColorId,
  getSizeId,
  getThemeType,
  normalizeId,
  setCustomizations,
} from '../../utils/ConfiguratorModuleUtils';
import { DEFAULT_THEME_TYPE, ThemeType } from '../../api/static/ThemeType';
import { DEFAULT_SCREEN_PRINTING_COLOR_ID } from '../../api/static/screenPrintingColors';
import { LAYOUT_STEP_LIST } from './configurator/layout';
import Vue from 'vue';
import { ROOT_CATEGORY_ID } from '../../api/static/categories';

const moduleFiles = import.meta.glob('./configurator/*.js', { eager: true });
const modules = {};

Object.entries(moduleFiles).forEach(([path, module]) => {
  modules[path.replace(/^\.\/configurator\/|\.js$/g, '')] = module.default;
});

const NAMESPACE = 'configurator';

export const { mapState, mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers(NAMESPACE);

/**
 * @type {function(string): string}
 */
export const prefixType = prefixTypeFactory(NAMESPACE);

export const ConfiguratorMutation = Object.freeze({
  SET_ACTIVE_STATE: 'setActiveState',
  SET_SELECTED_PRODUCT_INDEX: 'setSelectedProductIndex',
  SET_EXPRESS_DELIVERY: 'setExpressDelivery',
  SET_ZONE_CUSTOMIZATION: 'setZoneCustomization',
  SET_ZONE_CUSTOMIZATIONS: 'setZoneCustomizations',
  DELETE_ZONE_CUSTOMIZATION: 'deleteZoneCustomization',
  /** @deprecated */
  SET_FOCUSED_ZONE_ID: 'setFocusedZoneId',
  SET_FOCUSED_CUSTOMIZATION_ID: 'setFocusedCutomizationId',
  /**
   * @deprecated
   * @todo Remove this mutation name
   */
  SET_ACTIVE_ZONE_GROUP_INDEX: 'setActiveZoneGroupIndex',
  SET_ACTIVE_PRINTING_COLOR_ID: 'setActivePrintingColorId',
  SET_CTAS: 'setCtas',
  SET_CURRENT_CUSTOMER_PROJECT_DIRTY: 'setCurrentCustomerProjectDirty',
  SET_CURRENT_CUSTOMER_PROJECT_PRISTINE: 'setCurrentCustomerProjectPristine',
  SET_CSRF_TOKEN: 'setCsrfToken',
});

/**
 * @enum
 * @readonly
 */
export const ConfiguratorAction = Object.freeze({
  SET_ACTIVE_STATE: 'setActiveState',
  RESET_CUSTOMIZATIONS: 'resetCustomizations',
});

/**
 * @enum
 * @readonly
 */
export const ConfiguratorGetters = Object.freeze({
  GET_ACTIVE_PRODUCT_VARIANT: 'getActiveProductVariant',
  GET_ACTIVE_CONFIGURATION: 'getActiveConfiguration',
  GET_ACTIVE_SIZE: 'getActiveSize',
  GET_ACTIVE_COLOR: 'getActiveColor',
  GET_ACTIVE_GRADUATION_IMAGE: 'getActiveGraduationImage',
  GET_ACTIVE_GRADUATION_WIDTH: 'getActiveGraduationWidth',
  GET_ACTIVE_PRINTING_AREA: 'getActivePrintingArea',
  GET_ACTIVE_PRODUCT: 'getActiveProduct',
  GET_ACTIVE_QUANTITY: 'getActiveQuantity',
  GET_ACTIVE_CATEGORY: 'getActiveCategory',
  GET_ACTIVE_PARENT_CATEGORY: 'getActiveParentCategory',
  GET_ACTIVE_CATEGORY_PATH_NAME: 'getActiveCategoryPathName',
  GET_ACCOUNT_URL: 'getAccountUrl',
  GET_AUTH_URL: 'getAuthUrl',
  GET_CURRENT_CUSTOMER_PROJECT_ID: 'getCustomerProjectId',
  GET_SELECTED_PRODUCT_INDEX: 'getSelectedProductIndex',
  IS_EXPRESS_DELIVERY_AVAILABLE: 'isExpressDeliveryAvailable',
  IS_AUTHENTICATED: 'isAuthenticated',
  IS_CURRENT_CUSTOMER_PROJECT_DIRTY: 'isCurrentCustomerProjectDirty',
  GET_PRICE_PER_UNIT: 'getPricePerUnit',
  GET_PRICE_WITHOUT_VAT_PER_UNIT: 'getPriceWithoutVATPerUnit',
  GET_PRICE: 'getPrice',
  GET_PRICE_WITHOUT_VAT: 'getPriceWithoutVAT',
  /** @deprecated */
  GET_ACTIVE_ZONES: 'getActiveZones',
  GET_ACTIVE_TEMPLATE_ZONES: 'getActiveTemplateZones',
  /** @deprecated */
  GET_ACTIVE_ZONES_MERGED: 'getActiveZonesMerged',
  GET_ACTIVE_ZONE_CUSTOMIZATIONS: 'getActiveZoneCustomizations',
  GET_FOCUSED_ZONE: 'getFocusedZone',
  IS_ACTIVE_PRODUCT_VARIANT_SCREEN_PRINTING:
    'isActiveProductVariantScreenPrinting',
  GET_ACTIVE_SCREEN_PRINTING_COLOR: 'getActiveScreenPrintingColor',
  GET_AVAILABLE_COLORS: 'getAvailableColors',
  GET_CTAS_BY_STEP_ID: 'getCtasByStepId',
  GET_CURRENT_STEP_CTAS: 'getCurrentStepCtas',
  GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP:
    'getZoneCustomizationsGroupedByGroup',
  GET_GRADUATION_POSITION: 'getGraduationPosition',
  IS_GRADUATION_VISIBLE: 'isGraduationVisible',
  IS_GRADUATION_EDITABLE: 'isGraduationEditable',
  IS_GRADUATION_MOVABLE: 'isGraduationMovable',
  SHOULD_CENTER_CAMERA_ON_GRADUATION: 'shouldCenterCameraOnGraduation',
});

/**
 * @deprecated
 * @type {number}
 * @todo Remove this constant
 */
const DEFAULT_ZONE_GROUP_INDEX = 0;

export default {
  namespaced: true,
  modules,
  /**
   *
   * @returns {ConfiguratorState}
   */
  state() {
    return {
      themeType: DEFAULT_THEME_TYPE,
      activeCategoryId: null,
      activeSizeId: DEFAULT_SIZE_ID,
      activeColorId: DEFAULT_COLOR_ID,
      activeProductId: null,
      activeQuantityId: null,
      accountUrl: null,
      authUrl: null,
      isAuthenticated: false,
      selectedProductIndex: null,
      isExpressDeliveryEnabled: false,
      zoneCustomizationMap: {},
      zoneCustomizationList: {},
      currentCartItemId: null,
      focusedCustomizationId: null,
      activeScreenPrintingColorId: DEFAULT_SCREEN_PRINTING_COLOR_ID,
      currentOrderItemId: null,
      currentCustomerProjectId: null,
      isCurrentCustomerProjectDirty: false,
      ctas: null,
      graduationPosition: 80,
      isGraduationEnable: false,
      shouldCenterCameraOnGraduation: false,
      csrfToken: null, // CSRF token for add to cart, reusable for cross-sell
    };
  },
  mutations: {
    /**
     * @param {ConfiguratorState} state
     * @param {CategoryId} categoryId
     * @param {SizeId} sizeId
     * @param {ColorId} colorId
     * @param {ProductId} productId
     * @param {QuantityId} quantityId
     * @param themeType
     * @param {CartItemId} cartItemId
     * @param {ScreenPrintingColorId} screenPrintingColorId
     * @param {OrderItemId} orderItemId
     * @param {CustomerProjectId} customerProjectId
     * @param {boolean} isCustomerProjectDirty
     * @param {boolean} isAuthenticated
     * @param {string} authUrl
     * @param {string} accountUrl
     * @param {number} graduationPosition
     * @param {boolean} isGraduationEnable
     * @param {boolean} shouldCenterCameraOnGraduation
     */
    [ConfiguratorMutation.SET_ACTIVE_STATE](
      state,
      {
        categoryId = undefined,
        sizeId = undefined,
        colorId = undefined,
        productId = undefined,
        quantityId = undefined,
        themeType = undefined,
        cartItemId = undefined,
        screenPrintingColorId = undefined,
        orderItemId = undefined,
        customerProjectId = undefined,
        isCustomerProjectDirty = undefined,
        isAuthenticated = undefined,
        authUrl = undefined,
        accountUrl = undefined,
        graduationPosition = undefined,
        isGraduationEnable = undefined,
        shouldCenterCameraOnGraduation = undefined,
      }
    ) {
      if (categoryId !== undefined) {
        state.activeCategoryId = categoryId;

        if (categoryId !== state.activeCategoryId) {
          // Reset customizations on category change
          state.zoneCustomizationMap = {};
        }
      }

      if (sizeId !== undefined) {
        state.activeSizeId = sizeId;
      }

      if (colorId !== undefined) {
        state.activeColorId = colorId;
      }

      if (productId !== undefined) {
        state.activeProductId = productId;
      }

      if (quantityId !== undefined) {
        state.activeQuantityId = quantityId;
      }

      if (themeType !== undefined) {
        state.themeType = themeType;
      }

      if (cartItemId !== undefined) {
        state.currentCartItemId = cartItemId;
      }

      if (orderItemId !== undefined) {
        state.currentOrderItemId = orderItemId;
      }

      if (customerProjectId !== undefined) {
        state.currentCustomerProjectId = customerProjectId;
      }

      if (isCustomerProjectDirty !== undefined) {
        state.isCurrentCustomerProjectDirty = isCustomerProjectDirty;
      }

      if (screenPrintingColorId !== undefined) {
        state.activeScreenPrintingColorId = screenPrintingColorId;
      }

      if (isAuthenticated !== undefined) {
        state.isAuthenticated = isAuthenticated;
      }

      if (authUrl !== undefined) {
        state.authUrl = authUrl;
      }

      if (accountUrl !== undefined) {
        state.accountUrl = accountUrl;
      }

      if (graduationPosition !== undefined) {
        state.graduationPosition = graduationPosition;
        state.shouldCenterCameraOnGraduation = true;
      }

      if (isGraduationEnable !== undefined) {
        state.isGraduationEnable = isGraduationEnable;
        state.shouldCenterCameraOnGraduation = isGraduationEnable;
      }

      if (shouldCenterCameraOnGraduation !== undefined) {
        state.shouldCenterCameraOnGraduation = shouldCenterCameraOnGraduation;
      }
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {Number} payload
     */
    [ConfiguratorMutation.SET_SELECTED_PRODUCT_INDEX](state, payload) {
      state.selectedProductIndex = payload;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {Boolean} payload
     */
    [ConfiguratorMutation.SET_EXPRESS_DELIVERY](state, payload) {
      state.isExpressDeliveryEnabled = !!payload;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {ZoneCustomization|ZoneCustomizationText|ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText} customization
     * @param {ZoneGroupId} groupId
     * @param {ProductId} productId
     * @param {boolean} overwrite
     */
    [ConfiguratorMutation.SET_ZONE_CUSTOMIZATION](
      state,
      { customization, groupId, productId, overwrite = false }
    ) {
      setCustomizations(state, {
        list: [{ customization, groupId, productId }],
        overwrite,
      });
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {{groupId: ZoneGroupId, productId: ProductId, customization: ZoneCustomization|ZoneCustomizationText|ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText}[]} list
     * @param overwrite
     */
    [ConfiguratorMutation.SET_ZONE_CUSTOMIZATIONS](
      state,
      { list, overwrite = false }
    ) {
      setCustomizations(state, { list, overwrite });
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {ZoneCustomizationId} zoneCustomizationId
     */
    [ConfiguratorMutation.DELETE_ZONE_CUSTOMIZATION](
      state,
      zoneCustomizationId
    ) {
      if (
        !zoneCustomizationId ||
        state.zoneCustomizationMap[zoneCustomizationId] === undefined
      ) {
        throw new Error('Customization not found.');
      }

      let customizationIndex;

      const groupId = Object.keys(state.zoneCustomizationList).find(gId => {
        const cIndex = state.zoneCustomizationList[gId].findIndex(
          customizationId => customizationId === zoneCustomizationId
        );

        if (cIndex !== -1) {
          customizationIndex = cIndex;

          return true;
        }

        return false;
      });

      if (!groupId) {
        throw new Error('Zone group ID not found');
      }

      if (customizationIndex === undefined) {
        throw new Error('Customization index not found');
      }

      const newCustomizationList = state.zoneCustomizationList[groupId].slice();
      newCustomizationList.splice(customizationIndex, 1);

      // Remove customization ID from group list
      state.zoneCustomizationList = {
        ...state.zoneCustomizationList,
        [groupId]: newCustomizationList,
      };

      // Remove customization from customization map
      Vue.delete(state.zoneCustomizationMap, zoneCustomizationId);
    },
    /**
     * @param {ConfiguratorState} state
     * @param {ZoneId|null} zoneId
     * @deprecated
     */
    [ConfiguratorMutation.SET_FOCUSED_ZONE_ID](state, zoneId = null) {
      state.focusedZoneId = zoneId;
    },
    /**
     * @param {ConfiguratorState} state
     * @param {ZoneCustomizationId|null} customizationId
     */
    [ConfiguratorMutation.SET_FOCUSED_CUSTOMIZATION_ID](
      state,
      customizationId = null
    ) {
      state.focusedCustomizationId = customizationId;
    },
    /**
     * @param {ConfiguratorState} state
     * @param {number} activeZoneGroupIndex
     * @deprecated
     * @todo Remove this mutation (dead code)
     */
    [ConfiguratorMutation.SET_ACTIVE_ZONE_GROUP_INDEX](
      state,
      activeZoneGroupIndex = DEFAULT_ZONE_GROUP_INDEX
    ) {
      state.activeZoneGroupIndex = activeZoneGroupIndex;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {number} payload
     */
    [ConfiguratorMutation.SET_ACTIVE_PRINTING_COLOR_ID](
      state,
      payload = DEFAULT_SCREEN_PRINTING_COLOR_ID
    ) {
      state.activeScreenPrintingColorId = payload;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param {Cta[]} payload
     */
    [ConfiguratorMutation.SET_CTAS](state, payload) {
      state.ctas = payload;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorMutation.SET_CURRENT_CUSTOMER_PROJECT_DIRTY](state) {
      state.isCurrentCustomerProjectDirty = true;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorMutation.SET_CURRENT_CUSTOMER_PROJECT_PRISTINE](state) {
      state.isCurrentCustomerProjectDirty = false;
    },
    /**
     * @param {ConfiguratorState} state
     * @param {string} csrfToken
     */
    [ConfiguratorMutation.SET_CSRF_TOKEN](state, csrfToken) {
      state.csrfToken = csrfToken;
    },
  },
  actions: {
    /**
     * @param {ConfiguratorState} state
     * @param getters
     * @param commit
     * @param rootGetters
     * @param {number|string|CategoryId} categoryId
     * @param {SizeId} sizeId
     * @param {ProductId} productId
     * @param {ColorId} colorId
     * @param {QuantityId} quantityId
     * @param {Array} customizations
     * @param {boolean} fallbackWhenNotFound
     * @param {CartItemId} cartItemId
     * @param {ThemeType} themeType
     * @param screenPrintingColorId
     * @param {OrderItemId} orderItemId
     * @param {CustomerProjectId} customerProjectId
     * @param {boolean} isCustomerProjectDirty
     * @param {boolean} isAuthenticated
     * @param {string} authUrl
     * @param {string} accountUrl
     * @param {number} graduationPosition
     * @param {boolean} isGraduationEnable
     * @param {boolean} shouldCenterCameraOnGraduation
     * @todo Split this function into smaller ones
     */
    [ConfiguratorAction.SET_ACTIVE_STATE](
      { state, getters, commit, rootGetters },
      {
        categoryId,
        sizeId,
        productId,
        colorId,
        quantityId,
        customizations = undefined,
        fallbackWhenNotFound = false,
        cartItemId,
        themeType,
        screenPrintingColorId,
        orderItemId,
        customerProjectId,
        isCustomerProjectDirty,
        isAuthenticated,
        authUrl,
        accountUrl,
        graduationPosition,
        isGraduationEnable,
        shouldCenterCameraOnGraduation,
      } = {}
    ) {
      console.groupCollapsed(ConfiguratorAction.SET_ACTIVE_STATE);

      try {
        const activeState = {
          accountUrl,
          authUrl,
          isAuthenticated,
        };

        activeState.themeType = getThemeType(
          { state },
          themeType || state.themeType
        );

        const isCustomThemeType = activeState.themeType === ThemeType.CUSTOM;

        // Set category (if any)
        if (categoryId || (isCustomThemeType && state.activeCategoryId)) {
          let updatedCategoryId;

          // Skip category ID for custom theme types
          if (activeState.themeType === ThemeType.CUSTOM) {
            updatedCategoryId = null;
          } else {
            const normalizedCategoryId = normalizeId(categoryId);

            if (
              rootGetters[prefixGlassType(GlassesGetter.FIND_CATEGORY_BY_ID)](
                normalizedCategoryId
              )
            ) {
              updatedCategoryId = normalizedCategoryId;
            }
          }

          commit(ConfiguratorMutation.SET_ACTIVE_STATE, {
            categoryId: updatedCategoryId,
          });
        }

        // Set Print type ID
        activeState.screenPrintingColorId = getScreenPrintingColorId(
          { rootGetters },
          screenPrintingColorId,
          { screenPrintingColorId: state.activeScreenPrintingColorId }
        );

        // Set Size ID
        activeState.sizeId = getSizeId(
          { rootGetters },
          sizeId,
          {
            ...activeState,
            productId: state.activeProductId,
            sizeId: state.activeSizeId,
          },
          fallbackWhenNotFound
        );

        // Set Product ID
        activeState.productId = getProductId(
          { state, rootGetters },
          productId,
          {
            ...activeState,
            productId: state.activeProductId,
            categoryId: state.activeCategoryId,
          },
          fallbackWhenNotFound
        );

        // Set theme type
        /** @type {Product} */
        const product = rootGetters[
          prefixGlassType(GlassesGetter.FIND_PRODUCT_BY_ID)
        ](activeState.productId);

        activeState.themeType = product.isCustomizable
          ? ThemeType.CUSTOM
          : ThemeType.GENERIC;

        // Set Quantity ID
        activeState.quantityId = getQuantityId(
          { rootGetters },
          quantityId,
          {
            ...activeState,
            quantityId: state.activeQuantityId,
          },
          fallbackWhenNotFound
        );

        // Set color ID
        activeState.colorId = getColorId(
          { rootGetters },
          colorId,
          {
            ...activeState,
            colorId: state.activeColorId,
          },
          'color',
          fallbackWhenNotFound
        );

        // Set customer project ID (if any)
        if (customerProjectId) {
          const normalizedCustomerProjectId = normalizeId(customerProjectId);

          if (normalizedCustomerProjectId) {
            activeState.customerProjectId = normalizedCustomerProjectId;
          }
        }

        if (isCustomerProjectDirty !== undefined) {
          activeState.isCustomerProjectDirty = isCustomerProjectDirty;
        }

        // Set cart ID (if any)
        if (cartItemId) {
          const normalizedCartItemId = normalizeId(cartItemId);

          if (normalizedCartItemId) {
            activeState.cartItemId = normalizedCartItemId;
          }
        }

        // Set order ID (if any)
        if (orderItemId) {
          const normalizedOrderItemId = normalizeId(orderItemId);

          if (normalizedOrderItemId) {
            activeState.orderItemId = normalizedOrderItemId;
          }
        }

        // Store previous template zones in order to use them after committing the new state
        /** @type {ZoneGroupFull[]} */
        const previousZoneCustomizations =
          getters[ConfiguratorGetters.GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP];
        const isPreviousProductCustomizable = Boolean(
          getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT] &&
            getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT].isCustomizable
        );

        // Set graduationPosition
        activeState.graduationPosition = graduationPosition;

        // Set isGraduationEnable
        activeState.isGraduationEnable = isGraduationEnable;

        // Set shouldCenterCameraOnGraduation
        activeState.shouldCenterCameraOnGraduation =
          shouldCenterCameraOnGraduation;

        console.log(activeState);

        commit(ConfiguratorMutation.SET_ACTIVE_STATE, activeState);

        // Create new customizations from template zones
        /** @type {Zone[]} */
        const activeTemplateZones =
          getters[ConfiguratorGetters.GET_ACTIVE_TEMPLATE_ZONES];

        // Create virtual zones from template zones
        let virtualCustomizationPayloadList = activeTemplateZones.map(zone => {
          const customization = {
            type: zone.type,
            templateZoneId: zone.id,
          };

          if (zone.type === ZoneType.TEXT) {
            customization.text = undefined;
          } else if (zone.type === ZoneType.IMAGE) {
            customization.url = undefined;
          }

          return {
            groupId: zone.groupId === null ? undefined : zone.groupId,
            customization,
          };
        });

        /** @type {boolean} */
        const isNextProductCustomizable =
          getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT].isCustomizable;

        // Set customizations from URL (if provided)
        if (customizations) {
          if (Array.isArray(customizations)) {
            const templateZoneMap = activeTemplateZones.reduce(
              (acc, templateZone) => {
                acc[templateZone.id] = templateZone;

                return acc;
              },
              {}
            );

            if (isNextProductCustomizable) {
              // Check for every valid customization and prepend them to the customization list
              const _customizations = customizations.reduce(
                (acc, inputCustomization) => {
                  if (typeof inputCustomization === 'object') {
                    const templateZoneId = normalizeId(
                      inputCustomization.templateZoneId || inputCustomization.id
                    );

                    const templateZone = templateZoneMap[templateZoneId];

                    if (templateZone !== undefined) {
                      const { type, groupId } = templateZone;

                      const customization = {
                        id: undefined,
                        type,
                        templateZoneId,
                      };

                      const payload = {
                        groupId: groupId !== null ? groupId : undefined,
                        customization,
                      };

                      switch (type) {
                        case ZoneType.TEXT: {
                          if (
                            inputCustomization.text !== undefined &&
                            inputCustomization.text !== null
                          ) {
                            customization.text = inputCustomization.text;

                            if (inputCustomization.innerX) {
                              Object.assign(customization, {
                                x: inputCustomization.innerX,
                                y: inputCustomization.innerY,
                                scaleX: inputCustomization.innerScale,
                                scaleY: inputCustomization.innerScale,
                                rotation: inputCustomization.innerRotation,
                                fontSize: inputCustomization.fontSize,
                                typoId: inputCustomization.typoId,
                                bold: Boolean(inputCustomization.bold),
                                underlined: Boolean(
                                  inputCustomization.underlined
                                ),
                                italic: Boolean(inputCustomization.italic),
                                color: inputCustomization.color,
                              });

                              /** @type {ProductVariant|null} */
                              const activeProductVariant =
                                getters[
                                  ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT
                                ];

                              if (
                                activeProductVariant.printTypeId ===
                                  PrintTypeId.CMYK &&
                                inputCustomization.color
                              ) {
                                customization.color = inputCustomization.color;
                              }
                            }

                            acc.push(payload);

                            return acc;
                          }

                          break;
                        }
                        case ZoneType.IMAGE: {
                          if (
                            inputCustomization.url !== undefined &&
                            inputCustomization.url !== null
                          ) {
                            Object.assign(customization, {
                              url: inputCustomization.url,
                              badQuality: inputCustomization.badQuality,
                              x: inputCustomization.innerX,
                              y: inputCustomization.innerY,
                              width: inputCustomization.innerWidth,
                              height: inputCustomization.innerHeight,
                              scaleX: inputCustomization.innerScale,
                              scaleY: inputCustomization.innerScale,
                              rotation: inputCustomization.innerRotation,
                            });

                            acc.push(payload);

                            return acc;
                          }

                          break;
                        }
                        default: {
                          throw new Error('Invalid zone type');
                        }
                      }

                      console.warn(
                        `Invalid customization ${JSON.stringify(customization)}`
                      );
                    } else {
                      console.warn(
                        'Invalid customization (templateZoneId is invalid)'
                      );
                    }
                  } else {
                    console.warn(
                      `Invalid customization (Object expected, got ${typeof inputCustomization}). Skipping`
                    );
                  }

                  return acc;
                },
                []
              );

              // customizations are filled by url, we replace virtual with real ones.
              virtualCustomizationPayloadList = _customizations;
            } else {
              // Take only matching customizations and set their value
              const customizationMap = customizations.reduce(
                (acc, inputCustomization) => {
                  if (typeof inputCustomization === 'object') {
                    const templateZoneId = normalizeId(
                      inputCustomization.templateZoneId || inputCustomization.id
                    );

                    const templateZone = templateZoneMap[templateZoneId];

                    if (
                      templateZone !== undefined &&
                      // Do not overwrite customization if already defined
                      acc[templateZoneId] === undefined
                    ) {
                      const { type, groupId } = templateZone;

                      const customization = {
                        id: undefined,
                        type,
                        templateZoneId,
                      };

                      const payload = {
                        groupId: groupId !== null ? groupId : undefined,
                        customization,
                      };

                      switch (type) {
                        case ZoneType.TEXT: {
                          if (
                            inputCustomization.text !== undefined &&
                            inputCustomization.text !== null
                          ) {
                            customization.text = inputCustomization.text;

                            acc[templateZoneId] = payload;

                            return acc;
                          }

                          break;
                        }
                        case ZoneType.IMAGE: {
                          if (
                            inputCustomization.url !== undefined &&
                            inputCustomization.url !== null
                          ) {
                            customization.url = inputCustomization.url;
                            customization.badQuality =
                              inputCustomization.badQuality;

                            acc[templateZoneId] = payload;

                            return acc;
                          }

                          break;
                        }
                        default: {
                          throw new Error('Invalid zone type');
                        }
                      }

                      console.warn(
                        `Invalid customization ${JSON.stringify(customization)}`
                      );
                    } else {
                      console.warn(
                        'Invalid customization (templateZoneId is invalid)'
                      );
                    }
                  } else {
                    console.warn(
                      `Invalid customization (Object expected, got ${typeof inputCustomization}). Skipping`
                    );
                  }

                  return acc;
                },
                {}
              );

              virtualCustomizationPayloadList.map(payload => {
                const templateZoneId = payload.customization.templateZoneId;

                if (customizationMap[templateZoneId] !== undefined) {
                  payload.customization =
                    customizationMap[templateZoneId].customization;
                }

                return payload;
              });
            }
          } else {
            console.warn(
              `Invalid customization field (Array expected, got ${typeof customizations})`
            );
          }
        }
        // Transfer customizations from the previous state (if any)
        else if (previousZoneCustomizations.length) {
          /** @type {ZoneGroupFull[]} */
          const nextZoneCustomizations =
            getters[
              ConfiguratorGetters.GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP
            ];

          const isEmptyCustomization = customization =>
            (customization.type === ZoneType.TEXT &&
              (customization.text === undefined ||
                customization.text === null)) ||
            (customization.type === ZoneType.IMAGE &&
              customization.url === undefined);

          for (
            let groupIndex = 0, groupLength = nextZoneCustomizations.length;
            groupIndex < groupLength;
            groupIndex++
          ) {
            const previousGroup = previousZoneCustomizations[groupIndex];

            // Break loop if there is no previous group at given index
            if (previousGroup === undefined) {
              break;
            }

            // Using getter instead of group's zoneMap property to preserve template zone order as we assume the first zone item IS the template (if there are multiple zones of the same type)
            const templateZoneList =
              getters[ConfiguratorGetters.GET_ACTIVE_TEMPLATE_ZONES];

            if (!templateZoneList || templateZoneList.length === 0) {
              throw new Error('Invalid template zone list');
            }

            const zoneTypeList = Object.values(ZoneType);
            const isZoneTypeAvailable = zoneTypeList.reduce((acc, zoneType) => {
              acc[zoneType] = templateZoneList.some(
                zone => zone.type === zoneType
              );

              return acc;
            }, {});

            const templateZoneMap = zoneTypeList.reduce((acc, zoneType) => {
              // we assume that the index of the element in the array will be the groupIndex
              // meaning, there we'll be one kind of template per group.
              const zones = templateZoneList.filter(
                zone => zone.type === zoneType
              );

              if (zones.length) {
                acc[zoneType] = zones[groupIndex];
              }

              return acc;
            }, {});

            if (isNextProductCustomizable) {
              // Transfer each customization
              const inheritedCustomizations =
                previousGroup.customizations.reduce(
                  (acc, prevCustomization) => {
                    // Skip unavailable zone types
                    if (
                      isZoneTypeAvailable[prevCustomization.type] &&
                      !isEmptyCustomization(prevCustomization)
                    ) {
                      let inheritedProps;

                      if (isPreviousProductCustomizable) {
                        // if we change type of cup, zoneId change
                        inheritedProps = {
                          ...prevCustomization,
                          templateZoneId:
                            templateZoneMap &&
                            templateZoneMap[prevCustomization.type].id ===
                              prevCustomization.templateZoneId
                              ? prevCustomization.templateZoneId
                              : templateZoneMap[prevCustomization.type].id,
                        };
                      } else {
                        inheritedProps = {
                          templateZoneId:
                            templateZoneMap[prevCustomization.type].id,
                        };
                        switch (prevCustomization.type) {
                          case ZoneType.TEXT: {
                            inheritedProps.text = prevCustomization.text;

                            break;
                          }
                          case ZoneType.IMAGE: {
                            inheritedProps.url = prevCustomization.url;

                            break;
                          }
                          default: {
                            throw new Error('Unknown zone type');
                          }
                        }
                      }

                      const customization = {
                        ...inheritedProps,
                        id: undefined, // Reset customization ID (a new one will be generated automatically in the mutation)
                        type: prevCustomization.type,
                      };

                      const templateZone =
                        templateZoneMap[prevCustomization.type];

                      acc.push({
                        groupId:
                          templateZone.groupId !== null
                            ? templateZone.groupId
                            : undefined,
                        customization,
                      });
                    }

                    return acc;
                  },
                  []
                );

              // If the templateZoneId is the same, we'll replace
              // the customization to keep previous values
              const inheritedTemplateZones = inheritedCustomizations.map(
                inheritedCustomization => ({
                  templateZoneId:
                    inheritedCustomization.customization.templateZoneId,
                  groupIndex: groupIndex,
                })
              );

              // If we find an inheritedTemplateZones with the same groupIndex
              // and the same templateZoneId, we filter the template to prevent
              // duplicate with inheritedCustomizations
              if (inheritedTemplateZones.length !== 0) {
                virtualCustomizationPayloadList =
                  virtualCustomizationPayloadList.filter(
                    virtualCustomization => {
                      const groupInheritedTemplateZones =
                        inheritedTemplateZones.filter(
                          inheritedTemplateZone =>
                            groupIndex === inheritedTemplateZone.groupIndex
                        );
                      if (groupInheritedTemplateZones.length > 0) {
                        // if results in the same group, we don't return duplicate templateZones
                        return (
                          groupInheritedTemplateZones.filter(
                            inheritedTemplateZone =>
                              inheritedTemplateZone.templateZoneId ===
                              virtualCustomization.customization.templateZoneId
                          ).length === 0
                        );
                      } else {
                        // if nothing in this group, we don't filter anything
                        return true;
                      }
                    }
                  );
              }

              virtualCustomizationPayloadList.unshift(
                ...inheritedCustomizations
              );
            } else {
              // Keep only text zones and remove position settings.
              // Empty customizations are kept so that each type's array stays
              // positionally aligned with the previous zones: dropping them
              // would shift the remaining values onto the wrong target zone.
              const previousCustomizationsPerType =
                previousGroup.customizations.reduce(
                  (acc, previousCustomization) => {
                    if (acc[previousCustomization.type] === undefined) {
                      acc[previousCustomization.type] = [];
                    }

                    acc[previousCustomization.type].push(previousCustomization);

                    return acc;
                  },
                  {}
                );
              const previousCustomizationsPerTypeIndex = Object.keys(
                previousCustomizationsPerType
              ).reduce((acc, type) => {
                acc[type] = 0;

                return acc;
              }, {});

              virtualCustomizationPayloadList.map(payload => {
                const { customization } = payload;

                if (
                  // Check if there is a previous zone we can inherit from
                  previousCustomizationsPerType[customization.type] &&
                  previousCustomizationsPerTypeIndex[customization.type] <
                    previousCustomizationsPerType[customization.type].length
                ) {
                  const previousCustomization =
                    previousCustomizationsPerType[customization.type][
                      previousCustomizationsPerTypeIndex[customization.type]
                    ];

                  // Only inherit a non-empty value; an empty previous slot must
                  // leave the target zone untouched (it keeps its default value)
                  if (!isEmptyCustomization(previousCustomization)) {
                    switch (customization.type) {
                      case ZoneType.TEXT: {
                        customization.text = previousCustomization.text;

                        break;
                      }
                      case ZoneType.IMAGE: {
                        customization.url = previousCustomization.url;

                        break;
                      }
                      default: {
                        throw new Error('Unknown zone type');
                      }
                    }
                  }

                  previousCustomizationsPerTypeIndex[customization.type] =
                    previousCustomizationsPerTypeIndex[customization.type] + 1;
                }

                return payload;
              });
            }
          }
        }

        commit(ConfiguratorMutation.SET_ZONE_CUSTOMIZATIONS, {
          list: virtualCustomizationPayloadList,
          overwrite: true,
        });

        console.groupEnd();
      } catch (e) {
        console.groupEnd();

        throw e;
      }
    },
    /**
     * Reset all customizations to their default (empty) state
     * based on the active template zones.
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param commit
     */
    [ConfiguratorAction.RESET_CUSTOMIZATIONS]({ getters, commit }) {
      /** @type {Zone[]} */
      const activeTemplateZones =
        getters[ConfiguratorGetters.GET_ACTIVE_TEMPLATE_ZONES];

      if (!activeTemplateZones || activeTemplateZones.length === 0) {
        return;
      }

      // Create default (empty) customizations from template zones
      const defaultCustomizations = activeTemplateZones.map(zone => {
        const customization = {
          type: zone.type,
          templateZoneId: zone.id,
        };

        if (zone.type === ZoneType.TEXT) {
          customization.text = undefined;
        } else if (zone.type === ZoneType.IMAGE) {
          customization.url = undefined;
        }

        return {
          groupId: zone.groupId === null ? undefined : zone.groupId,
          customization,
        };
      });

      commit(ConfiguratorMutation.SET_ZONE_CUSTOMIZATIONS, {
        list: defaultCustomizations,
        overwrite: true,
      });

      // Mark project as pristine since customizations are resetted
      commit(ConfiguratorMutation.SET_CURRENT_CUSTOMER_PROJECT_PRISTINE);
    },
  },
  getters: {
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {ProductVariant|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      if (
        state.themeType &&
        state.activeProductId &&
        state.activeSizeId &&
        state.activeQuantityId
      ) {
        return rootGetters[prefixGlassType(GlassesGetter.FIND_PRODUCT_VARIANT)](
          state.themeType,
          state.activeSizeId,
          state.activeProductId,
          state.activeQuantityId
        );
      }

      return null;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Configuration|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_CONFIGURATION](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      return (
        (activeProductVariant &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_CONFIGURATION_BY_ID)](
            activeProductVariant.configurationId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Configuration|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_SIZE](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      return (
        (activeProductVariant &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_SIZE_BY_ID)](
            activeProductVariant.sizeId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Configuration|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_COLOR](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      return (
        rootGetters[prefixGlassType(GlassesGetter.FIND_COLOR_BY_ID)](
          state.activeColorId
        ) || null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Configuration|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_GRADUATION_IMAGE](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      return (
        rootGetters[prefixGlassType(GlassesGetter.FIND_GRADUATION_IMAGE)]() ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Configuration|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_GRADUATION_WIDTH](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      return (
        rootGetters[prefixGlassType(GlassesGetter.FIND_GRADUATION_WIDTH)]() ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Configuration|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_PRINTING_AREA](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      return (
        (activeProductVariant &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_PRINTING_AREA_BY_ID)](
            activeProductVariant.printingAreaId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.GET_ACCOUNT_URL](state) {
      return state.accountUrl;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.GET_AUTH_URL](state) {
      return state.authUrl;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.GET_CURRENT_CUSTOMER_PROJECT_ID](state) {
      return state.currentCustomerProjectId;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.GET_SELECTED_PRODUCT_INDEX](state) {
      return state.selectedProductIndex;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.IS_EXPRESS_DELIVERY_AVAILABLE](state) {
      return state.activeSizeId === SizeId.G25;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.IS_AUTHENTICATED](state) {
      return state.isAuthenticated;
    },
    /**
     *
     * @param {ConfiguratorState} state
     */
    [ConfiguratorGetters.IS_CURRENT_CUSTOMER_PROJECT_DIRTY](state) {
      return state.isCurrentCustomerProjectDirty;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {null|number}
     */
    [ConfiguratorGetters.GET_PRICE_PER_UNIT](state, getters) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      if (activeProductVariant) {
        return state.isExpressDeliveryEnabled &&
          getters[ConfiguratorGetters.IS_EXPRESS_DELIVERY_AVAILABLE]
          ? activeProductVariant.unitPrice * EXPRESS_DELIVERY_PERCENTAGE
          : activeProductVariant.unitPrice;
      }

      return null;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {null|number}
     */
    [ConfiguratorGetters.GET_PRICE_WITHOUT_VAT_PER_UNIT](state, getters) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      if (activeProductVariant) {
        return state.isExpressDeliveryEnabled &&
          getters[ConfiguratorGetters.IS_EXPRESS_DELIVERY_AVAILABLE]
          ? activeProductVariant.unitPriceWithoutVAT *
              EXPRESS_DELIVERY_PERCENTAGE
          : activeProductVariant.unitPriceWithoutVAT;
      }

      return null;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {null|number}
     */
    [ConfiguratorGetters.GET_PRICE](state, getters) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      if (activeProductVariant) {
        return state.isExpressDeliveryEnabled &&
          getters[ConfiguratorGetters.IS_EXPRESS_DELIVERY_AVAILABLE]
          ? activeProductVariant.price * EXPRESS_DELIVERY_PERCENTAGE
          : activeProductVariant.price;
      }

      return null;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {null|number}
     */
    [ConfiguratorGetters.GET_PRICE_WITHOUT_VAT](state, getters) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      if (activeProductVariant) {
        return activeProductVariant.priceWithoutVAT;
      }

      return null;
    },
    /**
     *
     * @param state
     * @param getters
     * @return {Zone[]|null}
     * @deprecated
     */
    [ConfiguratorGetters.GET_ACTIVE_ZONES](state, getters) {
      // TODO Remove this getter

      return getters[ConfiguratorGetters.GET_ACTIVE_TEMPLATE_ZONES];
    },
    /**
     *
     * @param state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @returns {Zone[]|undefined}
     */
    [ConfiguratorGetters.GET_ACTIVE_TEMPLATE_ZONES](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {Configuration|null} */
      const activeConfiguration =
        getters[ConfiguratorGetters.GET_ACTIVE_CONFIGURATION];

      if (activeConfiguration) {
        return rootGetters[prefixGlassesType(GlassesGetter.MAP_ZONES)](
          activeConfiguration.zones
        );
      }

      return undefined;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {(Zone|ZoneFullText|ZoneFullCustomImage|ZoneFullCustomText)[][]|null}
     * @deprecated
     * @todo Remove this getter
     */
    [ConfiguratorGetters.GET_ACTIVE_ZONES_MERGED](state, getters) {
      /** @type {Zone[]|null} */
      const activeZones = getters[ConfiguratorGetters.GET_ACTIVE_ZONES];

      if (activeZones) {
        return activeZones.map(zone => ({
          ...zone,
          _customization: state.zoneCustomizationMap[zone.id],
        }));
      }

      return null;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {{zone: Zone, customization: ZoneCustomizationText|ZoneCustomizationCustomConfigImage|ZoneCustomizationCustomConfigText}[]}
     */
    [ConfiguratorGetters.GET_ACTIVE_ZONE_CUSTOMIZATIONS](state, getters) {
      return getters[
        ConfiguratorGetters.GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP
      ].reduce((acc, { zoneMap, customizations }) => {
        acc.push(
          ...customizations.map(customization => ({
            zone: zoneMap[customization.templateZoneId],
            customization,
          }))
        );

        return acc;
      }, []);
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Product|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_PRODUCT](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      return (
        (activeProductVariant &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_PRODUCT_BY_ID)](
            activeProductVariant.productId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Quantity|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_QUANTITY](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      return (
        (activeProductVariant &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_QUANTITY_BY_ID)](
            activeProductVariant.quantityId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Category|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_CATEGORY](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      return (
        (state.activeCategoryId &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_CATEGORY_BY_ID)](
            state.activeCategoryId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {Category|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_PARENT_CATEGORY](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      const activeCategory = getters[ConfiguratorGetters.GET_ACTIVE_CATEGORY];

      return (
        (activeCategory &&
          activeCategory.parentId &&
          rootGetters[prefixGlassType(GlassesGetter.FIND_CATEGORY_BY_ID)](
            activeCategory.parentId
          )) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {Category|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_CATEGORY_PATH_NAME](state, getters) {
      const activeCategory = getters[ConfiguratorGetters.GET_ACTIVE_CATEGORY];
      const activeParentCategory =
        getters[ConfiguratorGetters.GET_ACTIVE_PARENT_CATEGORY];

      let categoryName = null;
      if (activeCategory) {
        categoryName = activeCategory.name;
      }
      if (
        activeParentCategory &&
        activeParentCategory.id !== ROOT_CATEGORY_ID
      ) {
        categoryName = activeParentCategory.name + ' > ' + categoryName;
      }

      return categoryName;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     */
    [ConfiguratorGetters.GET_FOCUSED_ZONE](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      const focusedCustomizationId = state.focusedCustomizationId;

      if (focusedCustomizationId) {
        const customization =
          state.zoneCustomizationMap[focusedCustomizationId];

        if (customization) {
          return rootGetters[prefixGlassType(GlassesGetter.FIND_ZONE_BY_ID)](
            customization.templateZoneId
          );
        }
      }

      return null;
    },
    [ConfiguratorGetters.IS_ACTIVE_PRODUCT_VARIANT_SCREEN_PRINTING](
      state,
      getters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      return (
        activeProductVariant &&
        activeProductVariant.printTypeId === PrintTypeId.SCREEN_PRINTING
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {ScreenPrintingColor|null}
     */
    [ConfiguratorGetters.GET_ACTIVE_SCREEN_PRINTING_COLOR](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      return (
        (state.activeScreenPrintingColorId &&
          rootGetters[
            prefixGlassType(GlassesGetter.FIND_SCREEN_PRINTING_COLOR_BY_ID)
          ](state.activeScreenPrintingColorId)) ||
        null
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {undefined}
     */
    [ConfiguratorGetters.GET_AVAILABLE_COLORS](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      /** @type {ProductVariant} */
      const activeProductVariant =
        getters[ConfiguratorGetters.GET_ACTIVE_PRODUCT_VARIANT];

      const activePrintTypeId =
        (activeProductVariant && activeProductVariant.printTypeId) ||
        DEFAULT_PRINT_TYPE_ID;

      return rootGetters[
        prefixGlassType(GlassesGetter.FIND_AVAILABLE_COLORS_BY_PRINT_TYPE_ID)
      ](activePrintTypeId);
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @return {function(*): *|null}
     */
    [ConfiguratorGetters.GET_CTAS_BY_STEP_ID](state) {
      return stepId => {
        const stepIndex =
          typeof stepId === 'number'
            ? stepId
            : LAYOUT_STEP_LIST.indexOf(stepId) + 1;
        return state.ctas
          ? state.ctas.filter(cta => cta.step === stepIndex)
          : null;
      };
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @return {Cta[]}
     */
    [ConfiguratorGetters.GET_CURRENT_STEP_CTAS](state, getters, rootState) {
      const activeStep = rootState.configurator.layout.activeStep;
      return getters[ConfiguratorGetters.GET_CTAS_BY_STEP_ID](activeStep);
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     */
    [ConfiguratorGetters.GET_ZONE_CUSTOMIZATIONS_GROUPED_BY_GROUP](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      const activeTemplateZones =
        getters[ConfiguratorGetters.GET_ACTIVE_TEMPLATE_ZONES] || [];
      const groups =
        rootGetters[prefixGlassType(GlassesGetter.GET_GROUP_LIST_FROM_ZONES)](
          activeTemplateZones
        );

      const productId = state.activeProductId;

      // Map groups (whether they have customizations or not)
      const groupMap = groups.reduce((acc, group) => {
        const groupId = getCustomizationGroupId(productId, group.id);

        const customizationList = state.zoneCustomizationList[groupId] || [];

        acc[groupId] = {
          ...group,
          customizations: [
            ...mapEntity(state.zoneCustomizationMap, customizationList),
          ],
        };

        return acc;
      }, {});

      return Object.values(groupMap).sort((a, b) => a.position < b.position);
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @return {number}
     */
    [ConfiguratorGetters.GET_GRADUATION_POSITION](state) {
      return state.graduationPosition;
    },
    [ConfiguratorGetters.SHOULD_CENTER_CAMERA_ON_GRADUATION](state) {
      return state.shouldCenterCameraOnGraduation;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {boolean}
     */
    [ConfiguratorGetters.IS_GRADUATION_EDITABLE](
      state,
      getters,
      rootState,
      rootGetters
    ) {
      const hasTexture =
        rootGetters[prefixGlassType(GlassesGetter.FIND_GRADUATION_IMAGE)]() !==
        undefined;

      const allowedOnModel =
        state.activeProductId &&
        [
          Number(process.env.MIX_FULL_CUSTOM_TOP_CMYK),
          Number(process.env.MIX_FULL_CUSTOM_CMYK),
        ].includes(state.activeProductId);

      return hasTexture && allowedOnModel;
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @param rootState
     * @param rootGetters
     * @return {boolean}
     */
    [ConfiguratorGetters.IS_GRADUATION_MOVABLE](state) {
      // Graduation position selection is only allowed on MIX_SINGLE_CMYK and MIX_FULL_CUSTOM_CMYK
      return (
        state.activeProductId &&
        [
          Number(process.env.MIX_FULL_CUSTOM_CMYK),
          Number(process.env.MIX_FULL_CUSTOM_TOP_CMYK),
        ].includes(state.activeProductId)
      );
    },
    /**
     *
     * @param {ConfiguratorState} state
     * @param getters
     * @return {boolean}@
     */
    [ConfiguratorGetters.IS_GRADUATION_VISIBLE](state, getters) {
      const isGraduationEditable =
        getters[ConfiguratorGetters.IS_GRADUATION_EDITABLE];

      return isGraduationEditable && state.isGraduationEnable;
    },
  },
};
